import { useCallback, useEffect, useState } from "react"

const LOAD_MODES = {
  IDLE: "idle",
  AUTO: "auto",
  MANUAL: "manual",
}

const SLOW_CONNECTION_TYPES = new Set(["slow-2g", "2g"])

const DEFAULT_MEDIA_POLICY = {
  ready: false,
  reducedMotion: false,
  constrained: false,
}

const getConnection = () => {
  if (typeof navigator === "undefined") return null

  return (
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection
  )
}

const getMediaPolicy = () => {
  const connection = getConnection()
  const mediaQuery =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null

  return {
    ready: true,
    reducedMotion: Boolean(mediaQuery?.matches),
    constrained: Boolean(
      connection?.saveData ||
        SLOW_CONNECTION_TYPES.has(connection?.effectiveType)
    ),
  }
}

const scheduleIdle = callback => {
  if (typeof window.requestIdleCallback === "function") {
    const idleId = window.requestIdleCallback(callback, { timeout: 1000 })

    return () => {
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId)
      }
    }
  }

  const timeoutId = window.setTimeout(callback, 0)

  return () => window.clearTimeout(timeoutId)
}

const useDeferredVideo = ({
  inView,
  isAboveFold,
  loadVideo = () => {},
  unloadVideo = () => {},
}) => {
  const [mediaPolicy, setMediaPolicy] = useState(DEFAULT_MEDIA_POLICY)
  const [loadMode, setLoadMode] = useState(LOAD_MODES.IDLE)
  const [playRequested, setPlayRequested] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return undefined

    const updateMediaPolicy = () => setMediaPolicy(getMediaPolicy())
    const mediaQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null
    const connection = getConnection()

    updateMediaPolicy()

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener("change", updateMediaPolicy)
    } else {
      mediaQuery?.addListener?.(updateMediaPolicy)
    }
    connection?.addEventListener?.("change", updateMediaPolicy)

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener("change", updateMediaPolicy)
      } else {
        mediaQuery?.removeListener?.(updateMediaPolicy)
      }
      connection?.removeEventListener?.("change", updateMediaPolicy)
    }
  }, [])

  useEffect(() => {
    if (!inView) {
      setLoadMode(LOAD_MODES.IDLE)
      setPlayRequested(false)
      unloadVideo()
      return undefined
    }

    if (
      !mediaPolicy.ready ||
      mediaPolicy.reducedMotion ||
      mediaPolicy.constrained ||
      loadMode !== LOAD_MODES.IDLE
    ) {
      return undefined
    }

    if (isAboveFold) {
      setLoadMode(LOAD_MODES.AUTO)
      return undefined
    }

    return scheduleIdle(() => setLoadMode(LOAD_MODES.AUTO))
  }, [
    inView,
    isAboveFold,
    loadMode,
    mediaPolicy.constrained,
    mediaPolicy.ready,
    mediaPolicy.reducedMotion,
    unloadVideo,
  ])

  useEffect(() => {
    if (
      inView &&
      loadMode === LOAD_MODES.AUTO &&
      mediaPolicy.ready &&
      (mediaPolicy.reducedMotion || mediaPolicy.constrained)
    ) {
      setLoadMode(LOAD_MODES.IDLE)
      setPlayRequested(false)
      unloadVideo()
    }
  }, [
    inView,
    loadMode,
    mediaPolicy.constrained,
    mediaPolicy.ready,
    mediaPolicy.reducedMotion,
    unloadVideo,
  ])

  useEffect(() => {
    if (loadMode === LOAD_MODES.AUTO && !inView) return
    if (loadMode !== LOAD_MODES.IDLE) loadVideo()
  }, [inView, loadMode, loadVideo])

  const requestVideoLoad = useCallback(({ play = false } = {}) => {
    if (play) setPlayRequested(true)
    setLoadMode(currentMode =>
      currentMode === LOAD_MODES.AUTO ? currentMode : LOAD_MODES.MANUAL
    )
  }, [])

  const clearPlayRequest = useCallback(() => setPlayRequested(false), [])

  return {
    autoPlayAllowed:
      loadMode === LOAD_MODES.AUTO &&
      inView &&
      mediaPolicy.ready &&
      !mediaPolicy.reducedMotion &&
      !mediaPolicy.constrained,
    clearPlayRequest,
    playRequested,
    requestVideoLoad,
  }
}

export default useDeferredVideo
